import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "../App.css";
import Modal from "../Component/Popup";
import Loader from "../Reusable/Loader/logoLoad";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

// The map is embedded in a Flutter WebView, so lay the layout image out to a
// fraction of the page width instead of its full pixel size. Plots are drawn as
// vector SVG over the image (no <canvas>), so it stays sharp at any zoom and has
// no pixel-memory ceiling to crash against.
const PAGE_WIDTH_RATIO = 0.85;

const calculateCenter = (coords) => {
    if (!coords || coords.length < 2) return { x: 0, y: 0 };
    const xCoords = coords.filter((_, i) => i % 2 === 0);
    const yCoords = coords.filter((_, i) => i % 2 !== 0);
    const xCenter = (Math.min(...xCoords) + Math.max(...xCoords)) / 2;
    const yCenter = (Math.min(...yCoords) + Math.max(...yCoords)) / 2;
    return { x: xCenter, y: yCenter };
};

const getCenterCoords = (areas) =>
    areas.map((area) => ({ ...area, center: calculateCenter(area.coords) }));

const fixPolyCoords = (coords) => {
    if (!coords || coords.length < 6) return coords;
    const points = [];
    for (let i = 0; i < coords.length; i += 2) {
        if (!isNaN(coords[i]) && !isNaN(coords[i + 1])) {
            points.push({ x: coords[i], y: coords[i + 1] });
        }
    }
    if (points.length < 3) return coords;

    const cx = points.reduce((sum, p) => sum + p.x, 0) / points.length;
    const cy = points.reduce((sum, p) => sum + p.y, 0) / points.length;

    points.sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx));

    const result = [];
    points.forEach((p) => {
        result.push(p.x, p.y);
    });
    return result;
};

const isCrossPlotItem = (item) => {
    if (!item) return false;
    return (
        item?.isCrossPlot == 1 ||
        item?.isCrossPlot === "1" ||
        item?.isCrossPlot === true ||
        item?.isCrossPlot === "true" ||
        item?.is_cross_plot == 1 ||
        item?.is_cross_plot === "1" ||
        item?.is_cross_plot === true ||
        item?.is_cross_plot === "true" ||
        item?.isCross == 1 ||
        item?.isCross === "1" ||
        item?.crossPlot == 1 ||
        item?.crossPlot === "1"
    );
};

const ColorCodes = (status) => {
    switch (status) {
        case 1:
            return {
                Dark: "rgba(0, 168, 76, 1)",
                Light: "rgba(24, 139, 24, 0.5)",
                Status: "Available",
            };
        case 2:
            return {
                Dark: "rgba(25, 153, 161, 1)",
                Light: "rgba(25, 153, 161, 0.5)",
                Status: "Registered",
            };
        case 3:
            return {
                Dark: "rgba(239, 72, 54, 1)",
                Light: "rgba(239, 72, 54, 0.5)",
                Status: "Allotted",
            };
        case 4:
            return {
                Dark: "rgba(255, 140, 0, 1)",
                Light: "rgba(255, 215, 0, 0.6)",
                Status: "Reserved",
            };
        case 5:
            return {
                Dark: "rgba(239, 72, 54, 1)",
                Light: "rgba(239, 72, 54, 0.5)",
                Status: "Allotted",
            };
        case 7:
            return {
                Dark: "rgb(193, 154, 107)",
                Light: "rgb(193, 154, 107)",
                Status: "Mortgage",
            };
        default:
            return {
                Dark: "rgba(0, 0, 0, 1)",
                Light: "rgba(255, 255, 255, 0.6)",
                Status: "Unknown",
            };
    }
};

function App() {
    const [loading, setLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const { Id } = useParams();
    const [extendedAreas, setExtendArea] = useState([]);
    const [PlotsCount, setPlotsCount] = useState({
        Available: 0,
        Alloted: 0,
        mortagage: 0,
        reserved: 0,
        registeredPlots: 0,
        total: 0,
    });
    const [layoutImage, setLayoutImage] = useState("");
    const [metagDetails, setMeta] = useState({ name: "", desc: "" });
    const [ModalData, setModalData] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [IsIos, setIsIos] = useState(false);
    const [modalAnchor, setModalAnchor] = useState({ x: 0, y: 0 });
    const [data, setdata] = useState([]);
    const [fetchError, setFetchError] = useState("");
    // Natural pixel size of the layout image, probed before we hand it to ImageMapper.
    const [imgNatural, setImgNatural] = useState({ width: 0, height: 0 });
    const [imgError, setImgError] = useState("");

    useEffect(() => {
        const userAgent = navigator.userAgent;
        if (/iPhone|iPad|iPod/i.test(userAgent)) {
            setIsIos(true);
        }
    }, []);

    useEffect(() => {
        if (!Id) {
            setLoading(false);
            setFetchError("No venture id in the URL.");
            return;
        }

        const controller = new AbortController();
        let cancelled = false;

        (async () => {
            setLoading(true);
            setFetchError("");
            try {
                const response = await fetch(
                    `https://apapi.adurigroup.com/api/v1/associate/getVenture/${encodeURIComponent(Id)}`,
                    { signal: controller.signal }
                );

                if (!response.ok) {
                    throw new Error(`Server returned ${response.status}`);
                }

                const res = await response.json();
                if (cancelled) return;

                if (res.Status != 1) {
                    throw new Error(res?.Message || "Venture data unavailable.");
                }

                const plots = Array.isArray(res.plots) ? res.plots : [];
                setdata(plots);
                setPlotsCount({
                    Alloted: res?.PlotsCount?.allotedPlots,
                    Available: res?.PlotsCount?.availablePlots,
                    registeredPlots: res?.PlotsCount?.registeredPlots,
                    mortagage: res?.PlotsCount?.mortgagePlots,
                    reserved: res?.PlotsCount?.reservedPlots,
                    total: res?.PlotsCount?.totalPlots,
                });
                setMeta({ name: res?.ventureName, desc: res?.ventureOverview });
                setExtendArea(
                    getCenterCoords(
                        plots.map((item) => ({
                            plot_number: item.plot_number,
                            coords: item.plot_coordinates
                                ? item.plot_coordinates.split(",").map(Number)
                                : [],
                            isCrossPlot: isCrossPlotItem(item),
                        }))
                    )
                );
                setLayoutImage(res?.layoutImage || "");
            } catch (error) {
                if (cancelled || error.name === "AbortError") return;
                console.error("Error fetching data:", error.message);
                setFetchError(error.message || "Could not load this venture.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [Id]);

    // Probe the layout image up front: we need its natural size to pick a canvas
    // size, and this is also the only place a broken/missing image URL surfaces.
    useEffect(() => {
        if (!layoutImage) return;
        let cancelled = false;
        setImgError("");
        setImgNatural({ width: 0, height: 0 });

        const probe = new window.Image();
        probe.onload = () => {
            if (cancelled) return;
            if (!probe.naturalWidth || !probe.naturalHeight) {
                setImgError("Layout image could not be decoded on this device.");
                return;
            }
            setImgNatural({ width: probe.naturalWidth, height: probe.naturalHeight });
        };
        probe.onerror = () => {
            if (!cancelled) setImgError("Layout image failed to load.");
        };
        probe.src = layoutImage;

        return () => {
            cancelled = true;
        };
    }, [layoutImage]);

    const isWeb = searchParams.get("type") === "web";

    // Draw the plots as SVG polygons over a plain <img>, instead of on a raster
    // <canvas>. A canvas has a hard WKWebView pixel ceiling (~16.7M px) and, more
    // importantly, gets re-rasterized on pinch-zoom — a large layout (e.g.
    // 3940x3200 with 565 plots) blows past the WebView's memory budget and
    // crashes on zoom. SVG is vector: no pixel-memory limit, stays razor sharp at
    // any zoom, and the <img> re-samples itself, so nothing crashes regardless of
    // how large the venture image is. All geometry is kept in the image's natural
    // coordinate space (the SVG viewBox); CSS fits the whole thing to the page.
    const svgPlots = useMemo(() => {
        const raw = data
            .map((item, index) => {
                const nums = item.plot_coordinates
                    ? item.plot_coordinates.split(",").map(Number)
                    : [];
                const coords = fixPolyCoords(nums);
                if (!coords || coords.length < 6) return null;

                const isCross = isCrossPlotItem(item);
                const fill = isCross
                    ? "rgba(255, 255, 255, 1)"
                    : item?.isMortgage == 1
                        ? ColorCodes(7).Dark
                        : ColorCodes(item.plot_status).Dark;

                let pts = "";
                let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
                for (let i = 0; i < coords.length - 1; i += 2) {
                    const x = coords[i], y = coords[i + 1];
                    pts += `${x},${y} `;
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                }
                const w = maxX - minX, h = maxY - minY;
                const label = String(item.plot_number ?? "");
                const chars = Math.max(1, label.length);

                return {
                    index,
                    points: pts.trim(),
                    fill,
                    cx: (minX + maxX) / 2,
                    cy: (minY + maxY) / 2,
                    w,
                    h,
                    chars,
                    bold: isCross,
                    label,
                    area: {
                        plot_status: item.plot_status,
                        sector_type: item.sector_type,
                        isMortgage: item?.isMortgage,
                        isCrossPlot: isCross ? 1 : 0,
                        facing_type: item.facing_type,
                        plot_number: item.plot_number,
                        dimensions: item.dimensions,
                        plot_area_sqy: item?.plot_area_sqy,
                        crossPlotSqy: item?.crossPlotSqy,
                        coords,
                    },
                };
            })
            .filter(Boolean);

        // Cap the label font to the typical (median) plot height so oversized
        // plots (e.g. a big corner plot) don't get an oversized number — they
        // stay at a normal plot's label size instead of scaling up with the box.
        const heights = raw.map((p) => p.h).filter((h) => h > 0).sort((a, b) => a - b);
        const medianH = heights.length ? heights[Math.floor(heights.length / 2)] : 0;
        const maxFs = medianH ? medianH * 0.42 : Infinity;
        // Border width in image (viewBox) units, i.e. proportional to plot size —
        // NOT a constant screen thickness. A fixed screen stroke looks fine on
        // desktop but turns thick/dark on the small mobile view, where the whole
        // map is scaled way down. Scaling it keeps borders looking the same
        // everywhere and thin at overview.
        const sw = medianH ? Math.max(0.5, medianH * 0.02) : 1;

        return raw.map((p) => {
            // Size the label to the plot box so it fits without overflowing,
            // then clamp to the shared max so large plots don't blow up.
            const fs = Math.max(
                4,
                Math.min(p.h * 0.38, (p.w * 0.7) / (p.chars * 0.6), maxFs)
            );
            return { ...p, fs, sw };
        });
    }, [data]);

    const handleSelect = (plot) => {
        setModalData(plot.area);
        setModalAnchor({ x: plot.cx, y: plot.cy });
        setShowModal(true);
    };

    // Zoom compensation: the popup lives inside the page, so pinch-zoom magnifies
    // it and it balloons. The page's resting zoom is 1 (viewport is
    // initial-scale=1), so dividing the popup by the CURRENT zoom keeps it a
    // constant on-screen size: at 3x zoom we render it at 1/3, the page magnifies
    // it 3x, net unchanged. Capped at 1 so it only ever shrinks, never grows.
    const [zoomComp, setZoomComp] = useState(1);
    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;
        const onZoom = () => {
            const s = vv.scale || 1;
            setZoomComp(Math.max(0.2, Math.min(1, 1 / s)));
        };
        onZoom();
        vv.addEventListener("resize", onZoom);
        vv.addEventListener("scroll", onZoom);
        return () => {
            vv.removeEventListener("resize", onZoom);
            vv.removeEventListener("scroll", onZoom);
        };
    }, []);

    // Size the popup to the VIEWPORT, not the map. Tying it to the map scale made
    // it shrink to nothing on the small mobile web view (and pinch-zoom couldn't
    // recover it). Instead, measure the popup's own intrinsic width and scale it
    // so it lands at a readable, consistent fraction of the screen everywhere.
    const modalRef = useRef(null);
    const containerRef = useRef(null);
    const metricsRef = useRef({ iw: 0, ih: 0 });
    const [modalScale, setModalScale] = useState(1);
    // Popup top-left in container pixels, computed so it never spills off an edge.
    const [modalPos, setModalPos] = useState({ left: 0, top: 0 });

    // 1) Measure the popup's intrinsic size and pick a base scale (~140px wide).
    // The .modal is position:absolute, so measure it directly — its offsetWidth/
    // Height are the un-transformed layout size. Re-measure next frame and on any
    // relayout, since a single measure on open can fire before it has laid out.
    useLayoutEffect(() => {
        if (!showModal) return;
        const inner = modalRef.current && modalRef.current.querySelector(".modal");
        if (!inner) return;
        const measure = () => {
            const iw = inner.offsetWidth;
            const ih = inner.offsetHeight;
            if (!iw) return;
            metricsRef.current = { iw, ih };
            const vw = document.documentElement.clientWidth || window.innerWidth || 360;
            const target = Math.min(vw * 0.9, 140);
            setModalScale(Math.max(0.3, Math.min(1.5, target / iw)));
        };
        measure();
        const raf = requestAnimationFrame(measure);
        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(measure);
            ro.observe(inner);
        }
        return () => {
            cancelAnimationFrame(raf);
            if (ro) ro.disconnect();
        };
    }, [showModal, ModalData]);

    // 2) Position the popup using its ACTUAL rendered scale (base * zoom
    // compensation), centered above the plot and clamped inside the map. This
    // re-runs when the zoom changes so the popup keeps tracking the plot at any
    // zoom, instead of drifting away as it shrinks.
    useLayoutEffect(() => {
        if (!showModal) return;
        const cont = containerRef.current;
        const { iw, ih } = metricsRef.current;
        if (!cont || !iw || !imgNatural.width) return;
        const eff = modalScale * zoomComp; // the scale actually applied
        const cw = cont.clientWidth;
        const ch = cont.clientHeight;
        const mw = iw * eff;
        const mh = ih * eff;
        const ax = (modalAnchor.x / imgNatural.width) * cw;
        const ay = (modalAnchor.y / imgNatural.height) * ch;
        const pad = 4;
        let left = ax - mw / 2; // centered on the plot
        let top = ay - mh - 6; // above the plot
        if (top < pad) top = ay + 6; // no room above → below
        left = Math.min(Math.max(pad, left), Math.max(pad, cw - mw - pad));
        top = Math.min(Math.max(pad, top), Math.max(pad, ch - mh - pad));
        setModalPos({ left, top });
    }, [showModal, ModalData, modalAnchor, modalScale, zoomComp, imgNatural.width, imgNatural.height]);

    if (loading) return <Loader />;

    return (
        <div className="wrapper_img">
            <Helmet>
                <meta charSet="utf-8" />
                <title>{metagDetails.name}</title>
                <meta property="og:description" content={metagDetails.desc || metagDetails.name} />
            </Helmet>

            {isWeb && (
                <div className="Count_table">
                    <h3>Total Plots: {PlotsCount.total}</h3>
                    <div className="table_data_wrapper">
                        <div className="table_data" style={{ background: "rgba(0, 168, 76, 1)" }}>
                            <h3>Available</h3>
                            <p>{PlotsCount.Available}</p>
                        </div>
                        <div className="table_data" style={{ background: "rgba(239, 72, 54, 1)" }}>
                            <h3>Alloted</h3>
                            <p>{PlotsCount.Alloted}</p>
                        </div>
                        <div className="table_data" style={{ background: "rgba(25, 153, 161, 1)" }}>
                            <h3>Registered</h3>
                            <p>{PlotsCount.registeredPlots || 0}</p>
                        </div>
                        <div className="table_data" style={{ background: "rgb(193, 154, 107)" }}>
                            <h3>Mortgage</h3>
                            <p>{PlotsCount.mortagage}</p>
                        </div>
                        <div className="table_data" style={{ background: "rgba(255, 140, 0, 1)" }}>
                            <h3>Reserved</h3>
                            <p>{PlotsCount.reserved}</p>
                        </div>
                    </div>
                </div>
            )}

            {fetchError || imgError || !layoutImage ? (
                <div className="map_error">
                    {fetchError || imgError || "This venture has no layout image."}
                </div>
            ) : !imgNatural.width ? (
                <Loader />
            ) : (
                <div
                    className="image-fit-wrapper"
                    style={{
                        width: `${PAGE_WIDTH_RATIO * 100}%`,
                        maxWidth: `${imgNatural.width}px`,
                        margin: "0 auto",
                        position: "relative",
                    }}
                    onClick={() => setShowModal(false)}>
                    {/* padding-bottom locks the aspect ratio (works on every
                        WebView, no aspect-ratio support needed) so the absolutely
                        positioned image + SVG fill it exactly. */}
                    <div
                        ref={containerRef}
                        className="image-container"
                        style={{
                            position: "relative",
                            width: "100%",
                            paddingBottom: `${(imgNatural.height / imgNatural.width) * 100}%`,
                        }}>
                        <img
                            src={layoutImage}
                            alt={metagDetails.name}
                            draggable={false}
                            style={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                display: "block",
                                pointerEvents: "none",
                                userSelect: "none",
                            }}
                        />

                        <svg
                            viewBox={`0 0 ${imgNatural.width} ${imgNatural.height}`}
                            preserveAspectRatio="xMidYMid meet"
                            style={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                            }}>
                            {svgPlots.map((p) => (
                                <polygon
                                    key={p.index}
                                    points={p.points}
                                    fill={p.fill}
                                    stroke="black"
                                    strokeWidth={p.sw}
                                    style={{ cursor: "pointer" }}
                                    onMouseEnter={() => handleSelect(p)}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelect(p);
                                    }}
                                />
                            ))}
                            {svgPlots.map((p) => (
                                <text
                                    key={`t-${p.index}`}
                                    x={p.cx}
                                    y={p.cy}
                                    fontSize={p.fs}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fontWeight={p.bold ? "bold" : "normal"}
                                    fill="black"
                                    style={{ pointerEvents: "none", userSelect: "none" }}>
                                    {p.label}
                                </text>
                            ))}
                        </svg>

                        {showModal && (
                            <div
                                ref={modalRef}
                                style={{
                                    position: "absolute",
                                    left: `${modalPos.left}px`,
                                    top: `${modalPos.top}px`,
                                    transform: `scale(${modalScale * zoomComp})`,
                                    transformOrigin: "top left",
                                    zIndex: 9999,
                                }}
                                onClick={(e) => e.stopPropagation()}>
                                <Modal
                                    ModalData={ModalData}
                                    x={0}
                                    y={0}
                                    onClose={() => setShowModal(false)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;
